import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { firestoreService } from '../../services/firestoreService';
import firebase from '../../firebase-config';
import './login.css';
import { trackEvent } from '../../config/googleAnalytics';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const planDetails = location.state?.planDetails;

  useEffect(() => {
    // Track login page view
    trackEvent('Auth', 'Login Page View', isSignUp ? 'SignUp Mode' : 'Login Mode');
    
    // Check for redirect result when component mounts
    const checkRedirectResult = async () => {
      try {
        const result = await authService.handleRedirectResult();
        if (result.success) {
          trackEvent('Auth', 'Social Login Success', 'Redirect');
          await handleSuccessfulLogin();
        } else if (result.error) {
          trackEvent('Auth', 'Social Login Error', result.error);
          setError('Failed to complete login. Please try again.');
        }
      } catch (error) {
        console.error('Error checking redirect result:', error);
        trackEvent('Auth', 'Redirect Error', error.message || 'Unknown error');
        setError('Failed to complete login. Please try again.');
      }
    };

    checkRedirectResult();
  }, [isSignUp]);

  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      let result;
      if (isSignUp) {
        trackEvent('Auth', 'Signup Attempt', 'Email/Password');
        result = await authService.signUpWithEmailAndPassword(email, password);
      } else {
        trackEvent('Auth', 'Login Attempt', 'Email/Password');
        // If this is a Gmail address, inform the user that Google auth might trigger
        if (email.endsWith('@gmail.com') || email.endsWith('@googlemail.com')) {
          setLoading(true);
          setError('Checking credentials... If you originally signed up with Google, you might see a Google login popup.');
        }
        result = await authService.signInWithEmailAndPassword(email, password);
      }
      
      if (result.success) {
        trackEvent('Auth', isSignUp ? 'Signup Success' : 'Login Success', 'Email/Password');
        handleSuccessfulLogin();
      }
    } catch (error) {
      let errorMessage = 'Failed to authenticate. Please try again.';
      console.error('Authentication error:', error.code, error.message);
      
      switch(error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists. Please sign in instead.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Invalid password. Please check your password and try again.';
          break;
        case 'auth/user-not-found':
          if (email.endsWith('@gmail.com') || email.endsWith('@googlemail.com')) {
            errorMessage = 'No account found with this email and password. If you originally signed up with Google, please use the "Continue with Google" button below.';
          } else {
            errorMessage = 'No account found with this email.';
          }
          break;
        case 'auth/invalid-login-credentials':
          if (email.endsWith('@gmail.com') || email.endsWith('@googlemail.com')) {
            errorMessage = 'Invalid credentials. If you originally signed up with Google, please use the "Continue with Google" button below.';
          } else {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          }
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Access to this account has been temporarily disabled due to many failed login attempts. You can reset your password or try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Login popup was blocked. Please allow popups for this website and try again.';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = 'Login popup was closed. Please try again and complete the Google sign in process.';
          break;
        default:
          errorMessage = error.message;
      }
      
      trackEvent('Auth', 'Auth Error', error.code || error.message);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    trackEvent('Auth', 'Forgot Password', 'Attempt');
    
    try {
      await authService.resetPassword(email);
      trackEvent('Auth', 'Forgot Password', 'Success');
      setError('Password reset email sent. Please check your inbox.');
      setShowForgotPassword(false);
    } catch (error) {
      trackEvent('Auth', 'Forgot Password', 'Error: ' + (error.code || error.message));
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setLoading(true);
    setError('');
    
    trackEvent('Auth', 'Social Login Attempt', provider);
    
    try {
      await authService.clearStorage();
      const result = await authService.loginWithProvider(provider);
      
      if (result?.success) {
        trackEvent('Auth', 'Social Login Success', provider);
        await handleSuccessfulLogin();
      }
    } catch (error) {
      let errorMessage = 'Failed to login. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        return; // Don't show error for user-cancelled requests
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Please allow popups for this website';
      } else {
        errorMessage = `Failed to login with ${provider}. Please try again.`;
      }
      
      trackEvent('Auth', 'Social Login Error', `${provider}: ${error.code || error.message}`);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async () => {
    try {
      const user = firebase.auth.currentUser;
      if (!user) {
        throw new Error('No user found after login');
      }

      // Get user subscription status
      const subscription = await firestoreService.getSubscription(user.uid);
      
      if (subscription?.isActive) {
        trackEvent('User', 'Subscription Status', 'Active: ' + subscription.type);
        
        const expiryDate = new Date(subscription.expiryDate);
        if (expiryDate > new Date()) {
          // Active subscription exists
          sessionStorage.setItem('userSubscription', JSON.stringify(subscription));
          localStorage.removeItem('selectedPlan');
          
          // Check if user was trying to access vectorshift chat
          const vectorshiftChat = localStorage.getItem('vectorshiftChat');
          if (vectorshiftChat === 'true') {
            navigate('/vectorshift-chat');
          } else {
            navigate('/chat');
          }
        } else {
          // Subscription exists but has expired
          trackEvent('User', 'Subscription Expired', subscription.type);
          localStorage.removeItem('userSubscription');
          navigate('/pricing');
        }
      } else {
        trackEvent('User', 'Subscription Status', 'No Active Subscription');
        
        // If user is coming from checkout, redirect back
        const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
        if (redirectAfterLogin) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectAfterLogin);
        } else {
          // Otherwise redirect to pricing
          navigate('/pricing');
        }
      }
    } catch (error) {
      console.error('Error after login:', error);
      trackEvent('Error', 'Post-Login Error', error.message);
      setError('An error occurred after login. Please try again.');
    }
  };

  const handleToggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    trackEvent('Auth', 'Toggle Mode', isSignUp ? 'To Login' : 'To Signup');
  };

  if (showForgotPassword) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Reset Password</h1>
            <div className="login-subtitle">Enter your email to receive a password reset link</div>
          </div>
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                className="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            {error && <div className="error-message">{error}</div>}
          </form>
          <button
            className="back-to-login"
            onClick={() => setShowForgotPassword(false)}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Rizzology-GPT</h1>
          <div className="login-subtitle">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </div>
        </div>

        <form onSubmit={handleEmailPasswordSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              className="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            {!isSignUp && (
              <div className="forgot-password">
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Processing...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <div className="social-login-section">
          <button
            type="button"
            className="social-login-button google"
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
          >
            <img src="/assets/google-icon.svg" alt="Google" className="social-icon" />
            Continue with Google
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="auth-switch">
          <p>{isSignUp ? 'Already have an account?' : 'Don\'t have an account?'}</p>
          <button
            type="button"
            className="auth-switch-button"
            onClick={handleToggleMode}
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login; 